import React, { useState } from 'react';
import theme from './DependenciesList.module.scss';
import { faHistory, faPencil, faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { Trans } from '@lingui/react/macro';
import { deleteDependency, DependencyGroupDto, searchDependencies } from '@/services/dependencies';
import { useAsyncState, useGetProjectId, useProjectRelativeRedirect } from '@/utils/hooks';
import { CardSection, SimpleCard, CardDropdownMenu } from '@/components/Cards';
import HistoryLogModal from '@/seller/components/HistoryLog/HistoryLogModal';
import { ProjectPermissionToggle } from '@/components/permissions';
import { CustomLink, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { searchOptionGroups } from '@/services/options/option-groups';
import { Button, ButtonBar, Dropdown, NoItemsCard } from '@ygtechnologies/rubus-ui';
import OptionGroupLabel from '@/seller/components/options/OptionGroupLabel/OptionGroupLabel';

// fake data
interface DependenciesListProps {}

const DependenciesList = (_: DependenciesListProps) => {
    const projectId = useGetProjectId();
    const navigate = useProjectRelativeRedirect();
    const [links, , reloadLinks] = useAsyncState(() => searchDependencies({ projectId: projectId! }), [projectId], null);

    const [optionGroups] = useAsyncState(() => searchOptionGroups({ projectId: projectId! }), [projectId], null);

    const [openLogModalId, setOpenLogModalId] = useState<number | null>(null);
    const renderOptionGroupBox = (optionGroupId: number) => {
        const optionGroup = optionGroups?.find((x) => x.id === optionGroupId);

        if (!optionGroup) {
            return null;
        }

        return (
            <div className={theme.optionGroup}>
                <div>
                    <OptionGroupLabel name={optionGroup.name} internalNote={optionGroup.internalNote} />
                </div>
                <div className={theme.info}>
                    {/* <Tags size="small" tags={optionGroup.tags} /> */}
                    {/* <LinkGroupUnitsTooltip data={optionGroup}>
                        <span>{optionGroup.residentialUnits.length} units</span>
                    </LinkGroupUnitsTooltip> */}
                </div>
            </div>
        );
    };

    const handleDelete = async (item: DependencyGroupDto) => {
        await deleteDependency(item.id);
        reloadLinks();
    };
    if (optionGroups === null || links === null) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <ProjectPermissionToggle condition={'isAdministrator'}>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Button as={CustomLink} to={`dependencies/create`} icon={faPlus} variant="tertiary">
                            <Trans>Create a new dependency</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ProjectPermissionToggle>

            {links.length === 0 ? (
                <NoItemsCard
                    title={<Trans>There are no dependencies!</Trans>}
                    description={<Trans>Want to create a dependency?</Trans>}
                    buttonProps={{
                        icon: faPlus,
                        onClick: () => navigate('options/dependencies/create'),
                        children: <Trans>Create a new dependency</Trans>,
                    }}
                />
            ) : (
                links.map((link) => (
                    <SimpleCard key={link.id}>
                        <CardSection width="30%" caption={<Trans>Name</Trans>}>
                            {link.name}
                        </CardSection>
                        <CardSection width="30%" caption={<Trans>From</Trans>}>
                            {renderOptionGroupBox(link.fromOptionGroupId)}
                        </CardSection>
                        <CardSection width="30%" caption={<Trans>To</Trans>}>
                            {renderOptionGroupBox(link.toOptionGroupId)}
                        </CardSection>

                        <CardDropdownMenu>
                            <Dropdown.Item as={CustomLink} to={`dependencies/edit/${link.id}`} icon={faPencil}>
                                <Trans>Edit</Trans>
                            </Dropdown.Item>
                            <Dropdown.Item icon={faHistory} onClick={() => setOpenLogModalId(link.id)}>
                                <Trans>View log</Trans>
                            </Dropdown.Item>
                            <Dropdown.Confirm
                                dialogTitle={<Trans>Delete</Trans>}
                                dialogBody={<Trans>Are you sure you want to delete "{link.name}"?</Trans>}
                                confirmButton={{ children: <Trans>Delete</Trans> }}
                                onConfirm={() => handleDelete(link)}
                                icon={faTrashAlt}>
                                <Trans>Delete</Trans>
                            </Dropdown.Confirm>
                        </CardDropdownMenu>
                    </SimpleCard>
                ))
            )}
            {openLogModalId && (
                <HistoryLogModal
                    hiddenColumns={['objectType']}
                    toggle={() => setOpenLogModalId(null)}
                    isOpen={openLogModalId !== null}
                    filter={{
                        projectId: projectId!,
                        objectType: 'Dependency',
                        objectIds: [openLogModalId],
                    }}
                />
            )}
        </>
    );
};

export default DependenciesList;
